import styled from "styled-components";
import watermark from "../../assets/images/watermark.svg";
import SVG from "../../components/ui/SVG";
import Button from "../../components/ui-v2/Button";
import Logo from "../../components/ui-v2/Logo";
import Search from "../../components/ui-v2/Inputs/Search";
import { useEffect, useRef, useState } from "react";
import { useNavigationManager } from "../../hooks/useNavigationManager";
import { ROUTES } from "../../router";
import {
  setAccounts,
  TAccount,
  TAccountData,
} from "../../redux/reducers/account";
import { localStorageConstants } from "../../utils/constants";
import apiService from "../../services/api";
import {
  getQuerystringParams,
  handleGetDataFromLocalStorage,
  handleParseQuerystrings,
  handleSetDataToLocalStorage,
} from "../../utils/miscellaneous";
import Spinner from "../../components/ui-v2/Spinner";

import { useTypedDispatch } from "../../hooks/useTypedDispatch";
import {
  clearInvoices,
  clearLastAccessedOrganizations,
  clearPaymentSlips,
} from "../../redux/reducers/globalState";
import axios, { CancelTokenSource } from "axios";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { fetchOrganizations } from "../../redux/actions";
import { clearOrganizations } from "../../redux/reducers/organizations";
import TimedInfiniteScroller from "../../components/TimedInfiniteScroller";

const Container = styled.main`
  position: relative;

  overflow: hidden;
  height: 100vh;

  svg#watermark {
    position: absolute;
    z-index: -1;
    width: calc(528vw / 14.4);
    height: calc(640vh / 8);
    left: 0;
  }

  @media (max-width: 1170px) {
    height: auto;
    width: 100vw;
    overflow-x: auto;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  height: 100%;
`;

const LogoArea = styled.div`
  margin-bottom: 32px;

  svg {
    width: 340px;
    height: 71.58px;
  }
`;

const PanelContainer = styled.section<{ $accountsCount: number }>`
  width: 774px;
  height: ${({ $accountsCount }) => ($accountsCount === 0 ? "228" : "474")}px;

  background: var(--color-light-50);

  border-radius: 4px;
  border: 1px solid var(--color-light-200);
  box-shadow: 0px 2px 8px 2px rgba(0, 0, 0, 0.05);

  & > * {
    margin: 24px;
  }

  & > h2 {
    text-align: center;
    font-family: var(--font-paragraph-default);
    font-size: 16px;
    color: var(--color-text-labels);
  }
`;

const PanelHeader = styled.div`
  & > * {
    margin-bottom: 10px;
  }

  h2 {
    font-family: var(--font-title-default);
    color: var(--color-dark-blue-600);
    font-size: 16px;
  }

  .separator {
    width: 30px;
    height: 2px;
    border-radius: 4px;
    background: var(--color-main-rose);
  }
`;

const NavigationArea = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & > *:first-child {
    width: 625px;
  }

  & > *:nth-child(2) {
    width: 85px;
    padding: 8px 10px;
    justify-content: space-evenly;
  }
`;

const PanelBody = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;

  max-height: 325px;
  overflow: auto;

  & > button {
    width: 230px;
    height: 54px;
    display: flex;
    justify-content: left;

    border-color: var(--color-light-200);
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);

    margin-bottom: 15px;

    &:hover {
      border-color: var(--color-main-rose);
      color: var(--color-main-rose);
      background: var(--color-light-0);
      transition: 0.2s ease-in-out;
    }
  }
`;

const Account: React.FC = () => {
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const [accounts, setLocalAccounts] = useState<TAccount[]>([]);
  const accountsData = useTypedSelector((state) => state.account.data);
  const accountsDataRef = useRef<TAccountData | null>(accountsData);

  const querystring = getQuerystringParams();

  const [hasMoreAccounts, setHasMoreAccounts] = useState(true);

  const reduxQuerystring = useTypedSelector((state) => state.querystring);

  const { goTo, setQuerystring } = useNavigationManager();

  const dispatch = useTypedDispatch();

  const handleOnClick = async (account: TAccount) => {
    const localSelectedAccount = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ACCOUNT
    ) as TAccount;

    const querystring = { ...reduxQuerystring };

    // Erase querystring search if present before navigating home

    delete querystring.modal;

    delete querystring.invoices_search;
    delete querystring.link_invoices_search;
    delete querystring.payment_slips_search;
    delete querystring.link_payment_slips_search;
    delete querystring.search;
    delete querystring.orgs_search;
    delete querystring.users_search;
    delete querystring.members_search;
    
    delete querystring.start_date;
    delete querystring.end_date;
    querystring.period = "all";

    if (querystring.organization_id) {
      querystring.organization_id = "";
    }

    if (account.uuid) {
      querystring.account_id = account.uuid;
    }
    setQuerystring(querystring);

    cancelTokenSource.current = axios.CancelToken.source();
    const accountsResponse = await apiService.getAccounts(
      cancelTokenSource.current.token
    );

    if (accountsResponse && accountsResponse.data) {
      dispatch(setAccounts(accountsResponse.data));
    } else {
      // setToastState({ type: "error", message: "Falha ao buscar contas." });
      console.error(
        "accountsResponse or accountsResponse.data is undefined",
        accountsResponse
      );
    }

    if (!localSelectedAccount || localSelectedAccount.uuid !== account.uuid) {
      new Promise(async (resolve) => {
        handleSetDataToLocalStorage(
          localStorageConstants.SELECT_ACCOUNT,
          account
        );

        localStorage.removeItem(localStorageConstants.SELECT_ORGANIZATION);
        localStorage.removeItem(
          localStorageConstants.LAST_ACCESSED_ORGANIZATIONS
        );

        dispatch(clearLastAccessedOrganizations());
        dispatch(clearOrganizations());
        dispatch(clearInvoices());
        dispatch(clearPaymentSlips());
        setTimeout(() => {
          resolve(true);
        }, 100);
      }).then(() => {
        setTimeout(() => {
          dispatch(
            fetchOrganizations({
              cancelToken: (cancelTokenSource.current as CancelTokenSource)
                .token,
            })
          )
            .unwrap()
            .then(() => {
              goTo(ROUTES.HOME);
            });
        });
      });
    } else {
      dispatch(
        fetchOrganizations({
          cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
        })
      )
        .unwrap()
        .then(() => {
          goTo(ROUTES.HOME);
        });
    }
  };

  const handleOnSearch = (search: string) => {
    const qs = { ...querystring, search };

    setQuerystring(qs);
    goTo(ROUTES.ACCOUNTS, qs);
    window.location.reload();
  };

  const loadMoreAccounts = async () => {
    const nextPage = (accountsDataRef.current?.current_page ?? 0) + 1;
    // If your API expects page as a direct argument:
    try {
      if (cancelTokenSource.current) {
        const response = await apiService.getAccounts(
          cancelTokenSource.current.token,
          querystring.search,
          nextPage
        );
        const newAccounts = response.data.results as TAccount[];

        if (response.data.count > 0) {
          setLocalAccounts((prev) => [
            ...prev,
            ...newAccounts.filter(
              (acc) => !prev.some((existing) => existing.uuid === acc.uuid)
            ),
          ]);
          accountsDataRef.current = {
            ...accountsDataRef.current,
            ...response.data,
            current_page: nextPage,
          };
        } else {
          setLocalAccounts([]); // Clear results
          accountsDataRef.current = null; // Reset pagination
          setHasMoreAccounts(false);
        }
      }
    } catch (e) {
      setHasMoreAccounts(false);
    }
  };

  useEffect(() => {
    // Reset accounts and pagination when entering the page
    setLocalAccounts([]);
    accountsDataRef.current = null;
    setHasMoreAccounts(true);
    cancelTokenSource.current = axios.CancelToken.source();
    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, []);

  return (
    <Container>
      <SVG id="watermark" src={watermark} />
      <ContentContainer>
        <LogoArea>
          <Logo />
        </LogoArea>
        <PanelContainer
          $accountsCount={
            accountsDataRef.current ? accountsDataRef.current.count : 1
          }
        >
          <PanelHeader>
            <h2>Buscar conta por nome</h2>
            <div className="separator"></div>
            <NavigationArea>
              <Search
                placeholder="Buscar conta"
                variant="default"
                onSearch={handleOnSearch}
                $ref={(inputRef, setSearchString) => {
                  const queryStrings = handleParseQuerystrings(
                    window.location.search
                  );
                  if (queryStrings.search && inputRef.current) {
                    const search = decodeURIComponent(
                      queryStrings.search[0].replace(/\+/g, " ")
                    );
                    inputRef.current.value = search;
                    setSearchString(search);
                  }
                }}
                onClear={() => {
                  const queryStrings = { ...getQuerystringParams() };
                  delete queryStrings.search;
                  setQuerystring(queryStrings);
                  window.location.reload();
                }}
              />
              <Button
                variant="outline"
                icon="logout"
                iconPosition="left"
                text="Sair"
                onClick={() => {
                  localStorage.clear();
                  goTo(ROUTES.LOGIN);
                }}
              />
            </NavigationArea>
          </PanelHeader>

          <TimedInfiniteScroller
            loadMore={loadMoreAccounts}
            hasMore={hasMoreAccounts}
            isLoading={false} // Set to true if you have a loading state
            delay={2000}
            loaderComponent={<></>}
            endMessage={null}
          >
            {" "}
            <PanelBody>
              {accounts.map((account, index) => (
                <Button
                  key={index}
                  text={account.name}
                  variant="secondary"
                  icon="noIcon"
                  onClick={() => handleOnClick(account)}
                />
              ))}
              {hasMoreAccounts && <Spinner style={{ width: 20, height: 20 }} />}

              {accounts.length === 0 && (
                <Button
                  style={{ visibility: "hidden" }}
                  text={"account.name"}
                  variant="secondary"
                  icon="noIcon"
                />
              )}
            </PanelBody>
          </TimedInfiniteScroller>
          {!hasMoreAccounts && accounts.length === 0 && (
            <h2>Nenhuma conta encontrada</h2>
          )}
        </PanelContainer>
      </ContentContainer>
    </Container>
  );
};

export default Account;
